/**
 * Monaco setup: core editor + all editor features + syntax highlighting for all basic languages.
 * Deliberately WITHOUT Monaco's built-in TS/JSON/CSS/HTML language services (no browser-side semantic workers), so only
 * the small editor worker is needed. Semantic TS/JS intelligence has exactly ONE source of truth: the language server
 * running on the paired Machine next to the source tree (M6.1, see ./lsp). Everything is bundled locally (no CDN),
 * compatible with our CSP.
 */
import * as monaco from "monaco-editor/editor/editor.api.js";
import "monaco-editor/features/register.all.js";
import "monaco-editor/languages/definitions/register.all.js";
import EditorWorker from "monaco-editor/editor/editor.worker.js?worker";

self.MonacoEnvironment = {
  getWorker: () => new EditorWorker(),
};

// JSON has no "basic language" (its full language service needs a worker). Register it with the
// JavaScript tokenizer: good-enough highlighting without diagnostics (no JSON language server in M6.1).
monaco.languages.register({
  id: "json",
  extensions: [".json", ".jsonc", ".code-workspace"],
  aliases: ["JSON"],
});
monaco.languages.onLanguage("json", () => {
  void import("monaco-editor/languages/definitions/javascript/javascript.js").then((mod) => {
    monaco.languages.setLanguageConfiguration("json", mod.conf);
    monaco.languages.setMonarchTokensProvider("json", mod.language);
  });
});

monaco.editor.defineTheme("racler-dark", {
  base: "vs-dark",
  inherit: true,
  rules: [],
  colors: { "editor.background": "#14161b" },
});

/** Pick a Monaco language id from the filename (extension or well-known names). */
export function languageForPath(path: string): string {
  const name = path.split("/").pop()?.toLowerCase() ?? "";
  const special: Record<string, string> = {
    dockerfile: "dockerfile",
    makefile: "shell",
    ".gitignore": "shell",
    ".env": "shell",
    ".zshrc": "shell",
    ".bashrc": "shell",
  };
  if (special[name]) return special[name];
  const dot = name.lastIndexOf(".");
  const ext = dot >= 0 ? name.slice(dot) : "";
  if (ext) {
    for (const lang of monaco.languages.getLanguages()) {
      if (lang.extensions?.includes(ext)) return lang.id;
    }
  }
  for (const lang of monaco.languages.getLanguages()) {
    if (lang.filenames?.some((f) => f.toLowerCase() === name)) return lang.id;
  }
  return "plaintext";
}

export { monaco };
